import { useState, useEffect } from "react";
import { CiCircleRemove } from "react-icons/ci";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { FaRegSave } from "react-icons/fa";
import { ImCancelCircle } from "react-icons/im";
import ButtonLightTextIcon from "../../../components/ui/ButtonLightTextIcon";
import ButtonTextIcon from "../../../components/ui/ButtonTextIcon";
import { API_URL } from "../../../utilities/constants";
import { useUI } from "../../../context/ui";
import FullPageLoader from "../../../components/ui/FullPageLoader";

const Label = ({ title, required, style }) => (
  <p
    className="color-primary f-w-600"
    style={{ paddingBottom: "5px", ...style }}
  >
    {title}
    {required && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
  </p>
);

const UpdateNotification = ({ setShow, id, callback }) => {
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    name: "",
    subject: "",
    body: "",
  });
  const [charCount, setCharCount] = useState({ subject: 0, body: 0 });
  const {
    addToastMessage,
    setNewNotification,
    setShowNotification,
    addNotification,
  } = useUI();

  const fetchTemplate = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/1.0.0/template/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.result?.responseCode === 200) {
          const { name, subject, body } = data.data;
          setInitialValues({
            name: name || "",
            subject: subject || "",
            body: body || "",
          });
          setCharCount({
            subject: subject?.length || 0,
            body: body?.length || 0,
          });
        }
      }
    } catch (e) {
      addToastMessage({ status: false, message: "Error fetching template." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchTemplate(id);
    else setLoading(false);
  }, [id]);

  const formValidation = Yup.object().shape({
    name: Yup.string()
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name can't exceed 50 characters")
      .matches(/^[a-zA-Z\s]+$/, "Name can only contain letters and spaces")
      .required("Name is required"),
    subject: Yup.string()
      .min(5, "Subject must be at least 5 characters")
      .max(50, "Subject can't exceed 50 characters")
      .required("Subject is required"),
    body: Yup.string()
      .min(10, "Body must be at least 10 characters")
      .max(150, "Body can't exceed 150 characters")
      .required("Body is required"),
  });

  const handleOnSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/1.0.0/template/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
        body: JSON.stringify(values),
      });
      if (response.ok) {
        addToastMessage({
          status: true,
          message: "Template updated successfully.",
        });
        callback();
        setShow(false);
      }
    } catch (e) {
      addToastMessage({ status: false, message: "Error updating template." });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="responsive-container flex pos-rel"
      style={{ minHeight: "60vh", flexDirection: "column" }}
    >
      <FullPageLoader isActive={loading} />
      <CiCircleRemove
        onClick={() => setShow(false)}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          fontSize: "32px",
        }}
      />
      <h6 className="t-a-c color-primary">Update Push Notification</h6>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={formValidation}
        onSubmit={handleOnSubmit}
      >
        {({ values, errors, touched, handleChange }) => (
          <Form>
            <div
              className="flex responsive-container"
              style={{ gap: "12px", padding: "20px", width: "400px" }}
            >
              {["name", "subject", "body"].map((field) => (
                <div className="col-1-1" key={field}>
                  <Label
                    title={field.charAt(0).toUpperCase() + field.slice(1)}
                    required
                  />
                  <Field
                    as={field === "body" ? "textarea" : "input"}
                    type={field !== "body" ? "text" : undefined}
                    name={field}
                    placeholder={`Enter ${field}`}
                    maxLength={
                      field === "subject"
                        ? 50
                        : field === "body"
                        ? 150
                        : undefined
                    }
                    value={values[field]}
                    onChange={(e) => {
                      handleChange(e);
                      if (field === "subject" || field === "body") {
                        setCharCount((prev) => ({
                          ...prev,
                          [field]: e.target.value.length,
                        }));
                      }
                    }}
                    style={{ backgroundColor: "#f1f1f1" }}
                  />
                  {(field === "subject" || field === "body") && (
                    <small
                      className="flex center-right"
                      style={{ fontSize: "12px", textAlign: "right" }}
                    >{`${
                      (field === "subject" ? 50 : 150) - charCount[field]
                    } characters left`}</small>
                  )}
                  {errors[field] && touched[field] && (
                    <p style={{ color: "red", fontSize: "12px" }}>
                      {errors[field]}
                    </p>
                  )}
                </div>
              ))}

              <div
                className="flex center-center col-1-1"
                style={{ gap: "20px" }}
              >
                <ButtonLightTextIcon
                  title="Cancel"
                  type="button"
                  icon={<ImCancelCircle />}
                  onClick={() => setShow(false)}
                />
                <ButtonTextIcon
                  title={loading ? "Updating..." : "Update"}
                  type="submit"
                  icon={<FaRegSave />}
                  disabled={loading}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UpdateNotification;
